/**
 * useServiceRequests Hook
 *
 * Real-time Firestore listener for the service_requests collection.
 * Tenanted, sanitized (Timestamp → ISO), with stats aggregation helpers.
 */

import { getTenantIdSync } from '@/lib/tenant';
import { useState, useEffect, useMemo } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
  QueryConstraint,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { tenantIdReadUnion } from './useTenantId';
import type {
  ServiceRequest,
  ServiceRequestStage,
  ServiceRequestPriority,
  ServiceRequestStats,
} from '../types/service-request.types';
import {
  ALL_SERVICE_REQUEST_STAGES,
} from '../types/service-request.types';

function toISOString(value: unknown): string {
  if (!value) return '';
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (
    typeof value === 'object' &&
    value !== null &&
    'seconds' in value &&
    'nanoseconds' in value
  ) {
    return new Date((value as { seconds: number }).seconds * 1000).toISOString();
  }
  if (typeof value === 'string') return value;
  return String(value);
}

/**
 * Sanitize a raw Firestore service_requests document. Mirrors sanitizeFault.
 */
function sanitizeServiceRequest(raw: Record<string, unknown>): ServiceRequest {
  // Loosely-typed nested entries from Firestore; narrowed as we touch them.
  type TsEntry = Record<string, unknown>;
  const timestampFields = [
    'createdAt',
    'updatedAt',
    'intakeDate',
    'completedAt',
    'archivedAt',
    'sentToBillingAt',
    'billedAt',
    'paidAt',
  ];
  for (const field of timestampFields) {
    if (raw[field] && typeof raw[field] !== 'string') {
      raw[field] = toISOString(raw[field]);
    }
  }

  // Safe defaults
  if (!raw.customerName || typeof raw.customerName !== 'string') {
    raw.customerName = raw.customerName ? String(raw.customerName) : 'Unknown';
  }
  if (!raw.title || typeof raw.title !== 'string') {
    raw.title = raw.title ? String(raw.title) : '';
  }
  if (!raw.description || typeof raw.description !== 'string') {
    raw.description = raw.description ? String(raw.description) : '';
  }

  if (!Array.isArray(raw.activityLog)) raw.activityLog = [];
  if (!Array.isArray(raw.customerUpdates)) raw.customerUpdates = [];

  for (const entry of raw.activityLog as TsEntry[]) {
    if (entry?.timestamp && typeof entry.timestamp !== 'string') {
      entry.timestamp = toISOString(entry.timestamp);
    }
  }
  for (const entry of raw.customerUpdates as TsEntry[]) {
    if (entry?.timestamp && typeof entry.timestamp !== 'string') {
      entry.timestamp = toISOString(entry.timestamp);
    }
    if (entry?.nextUpdateDue && typeof entry.nextUpdateDue !== 'string') {
      entry.nextUpdateDue = toISOString(entry.nextUpdateDue);
    }
  }

  return raw as unknown as ServiceRequest;
}

function getTenantId(): string {
  return getTenantIdSync();
}

/**
 * Main hook — real-time list with optional filters.
 */
export function useServiceRequests(options?: {
  includeArchived?: boolean;
  stage?: ServiceRequestStage;
  customerId?: string;
}) {
  const [requests, setRequests] = useState<ServiceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const tenantId = getTenantId();
    // Union-read (not strict `==`) so legacy-alias docs still surface — matches
    // every other tenanted hook in the app and the documented policy.
    const constraints: QueryConstraint[] = [
      where('tenantId', 'in', tenantIdReadUnion(tenantId)),
    ];

    if (!options?.includeArchived) {
      constraints.push(where('archived', '==', false));
    }
    if (options?.stage) {
      constraints.push(where('stage', '==', options.stage));
    }
    if (options?.customerId) {
      constraints.push(where('customerId', '==', options.customerId));
    }

    // NOTE: no `orderBy('createdAt')` — it silently drops requests missing the
    // field. Sort client-side (newest first) after sanitisation instead.
    const q = query(collection(db, 'service_requests'), ...constraints);

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs
          .map((d) => sanitizeServiceRequest({ id: d.id, ...d.data() }))
          .sort((a, b) =>
            String(b.createdAt || '').localeCompare(String(a.createdAt || '')),
          );
        setRequests(data);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching service requests:', err);
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [options?.includeArchived, options?.stage, options?.customerId]);

  return { requests, loading, error };
}

/**
 * Get all service requests flagged for billing (sentToBilling: true).
 * Includes archived so completed+billable requests still surface.
 */
export function useServiceRequestsForBilling() {
  const [requests, setRequests] = useState<ServiceRequest[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const tenantId = getTenantId();
    const q = query(
      collection(db, 'service_requests'),
      where('tenantId', '==', tenantId),
      where('sentToBilling', '==', true)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((d) =>
          sanitizeServiceRequest({ id: d.id, ...d.data() })
        );
        // Sort by sentToBillingAt desc client-side (avoids index requirement)
        data.sort((a, b) =>
          (b.sentToBillingAt || b.createdAt).localeCompare(
            a.sentToBillingAt || a.createdAt
          )
        );
        setRequests(data);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching service requests for billing:', err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  return { requests, loading };
}

/**
 * Compute aggregate stats from an array of service requests
 */
export function computeServiceRequestStats(
  requests: ServiceRequest[]
): ServiceRequestStats {
  const byStage = ALL_SERVICE_REQUEST_STAGES.reduce(
    (acc, s) => ({ ...acc, [s]: 0 }),
    {} as Record<ServiceRequestStage, number>
  );
  const byPriority: Record<ServiceRequestPriority, number> = {
    low: 0,
    medium: 0,
    high: 0,
    urgent: 0,
  };
  // Not pre-seeded with the telco keys: request types come from the tenant's
  // trade pack now, so the set isn't knowable here. Absent key = zero.
  const byType: Record<string, number> = {};

  let awaitingAction = 0;
  let completionHoursTotal = 0;
  let completedCount = 0;
  let completedThisWeek = 0;

  const weekAgoMs = Date.now() - 7 * 24 * 60 * 60 * 1000;

  for (const r of requests) {
    byStage[r.stage] = (byStage[r.stage] || 0) + 1;
    byPriority[r.priority] = (byPriority[r.priority] || 0) + 1;
    byType[r.requestType] = (byType[r.requestType] || 0) + 1;

    // "Awaiting action" = stages where the office still has something to do.
    // Standard: Intake, In-Progress. NBN: Intake, Check-Status.
    if (
      r.stage === '01-Intake' ||
      r.stage === '02-In-Progress' ||
      r.stage === 'NBN-01-Intake' ||
      r.stage === 'NBN-04-Check-Status'
    ) {
      awaitingAction++;
    }

    if (r.completedAt && r.createdAt) {
      const createdMs = new Date(r.createdAt).getTime();
      const completedMs = new Date(r.completedAt).getTime();
      if (!isNaN(createdMs) && !isNaN(completedMs) && completedMs >= createdMs) {
        completionHoursTotal += (completedMs - createdMs) / (1000 * 60 * 60);
        completedCount++;
        if (completedMs >= weekAgoMs) {
          completedThisWeek++;
        }
      }
    }
  }

  const totalOpen = requests.filter((r) => !r.archived).length;

  return {
    totalOpen,
    byStage,
    byPriority,
    byType,
    averageCompletionHours:
      completedCount > 0 ? completionHoursTotal / completedCount : 0,
    completedThisWeek,
    awaitingAction,
  };
}

/**
 * Stats hook — derives live stats from an existing list of requests
 */
export function useServiceRequestStats(requests: ServiceRequest[]) {
  return useMemo(() => computeServiceRequestStats(requests), [requests]);
}
